import { handlerDrawerOpen } from "api/menu";
import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

const RightPanelContext = createContext(null);

export const RightPanelProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [drawerType, setDrawerType] = useState(null);
  const [drawerProps, setDrawerProps] = useState({});
  const [drawerTitle, setDrawerTitle] = useState("");
  const [onCloseCallback, setOnCloseCallback] = useState(null);

  useEffect(() => {
    if (isOpen) {
      handlerDrawerOpen(false);
    }
  }, [isOpen]);

  const openDrawer = useCallback(
    (type, { title = "", props = {}, onClose = null } = {}) => {
      const isUpdatingSameDrawer = isOpen && drawerType === type;

      // Only call previous onClose when replacing with a different drawer.
      // Reopening the same drawer type is used to refresh props (e.g. dose tabs
      // in the medicine drawer) and should not reset the current editor state.
      if (!isUpdatingSameDrawer && onCloseCallback) {
        onCloseCallback();
      }

      setDrawerType(type);
      setDrawerProps(props);
      setDrawerTitle(title);
      setOnCloseCallback(() => onClose);
      setIsOpen(true);
    },
    [drawerType, isOpen, onCloseCallback]
  );

  const closeDrawer = useCallback(() => {
    if (onCloseCallback) {
      onCloseCallback();
    }

    setIsOpen(false);

    // wait for animation before clearing
    setTimeout(() => {
      setDrawerType(null);
      setDrawerProps({});
      setDrawerTitle("");
      setOnCloseCallback(null);
    }, 300);
  }, [onCloseCallback]);

  return (
    <RightPanelContext.Provider
      value={{
        isOpen,
        drawerType,
        drawerProps,
        drawerTitle,
        openDrawer,
        closeDrawer,
      }}
    >
      {children}
    </RightPanelContext.Provider>
  );
};

export const useRightPanel = () => {
  const context = useContext(RightPanelContext);
  if (!context) {
    throw new Error("useRightPanel must be used within RightPanelProvider");
  }
  return context;
};